(() => {
  'use strict';

  /*****************************************
   * CONFIG
   *****************************************/
  const GIFT_CARD_VARIANT_ID = 51770912440662;          // gift wrap product variant ID
  const SLIDECART_ROOT_SELECTOR = '#slidecarthq';
  const DEBUG                = false;

  /*****************************************
   * INTERNAL STATE
   *****************************************/
  let cartCache = null;
  let cartFetchPromise = null;
  let cartRevision = 0;
  let lastSyncState = { requiredQty: null, existingQty: null };

  // internal flag so we don't double-run sync in SLIDECART_UPDATED
  window.__GW_INTERNAL_REFRESH__ = false;

  /*****************************************
   * LOGGING HELPERS
   *****************************************/
  const log  = (...a) => { if (DEBUG) console.log('[giftwrap]', ...a); };
  const warn = (...a) => console.warn('[giftwrap]', ...a);
  const err  = (...a) => console.error('[giftwrap]', ...a);
  const trace = (label, payload) => {
    if (!DEBUG) return;
    try {
      console.groupCollapsed(`[giftwrap] ${label}`);
      console.log(payload);
      console.groupEnd();
    } catch {
      console.log('[giftwrap]', label, payload);
    }
  };

  /*****************************************
   * FEATURE TOGGLE
   *****************************************/
  // We expose a checkbox in theme settings as `settings.enable_gift_wrapping`,
  // and write it into theme.liquid:
  //   window.GIFTWRAP_ENABLED = {{ settings.enable_gift_wrapping | json }};
  //
  // We wrap all behavior with this helper.
  function giftwrapFeatureOn() {
    if (typeof window.GIFTWRAP_ENABLED === 'boolean') {
      return window.GIFTWRAP_ENABLED;
    }
    // fallback default is ON, if missing
    return true;
  }

  async function safeJson(res, labelForLogs) {
    try {
      return await res.json();
    } catch (parseErr) {
      // fallback for weird content-types / HTML
      let text = '';
      try { text = await res.text(); } catch (_) {}
      const ct = res.headers.get('content-type') || '';
      warn(labelForLogs || 'safeJson', 'non-JSON response', {
        status: res.status,
        ct,
        snippet: text ? text.slice(0, 200) : '(empty)'
      });
      return {};
    }
  }

  /*****************************************
   * CART FETCH / CACHE
   *****************************************/
  function hardFetchCart() {
    if (cartFetchPromise) return cartFetchPromise;

    const requestRevision = cartRevision;
    cartFetchPromise = (async () => {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      });
      const json = await safeJson(res, 'hardFetchCart()');
      trace('hardFetchCart()', json);
      // Do not let an older request overwrite fresher cart data supplied by a
      // SlideCart callback or returned by a cart mutation.
      if (cartRevision === requestRevision) {
        cartCache = json;
        cartRevision += 1;
      }
      return cartCache;
    })().finally(() => {
      cartFetchPromise = null;
    });

    return cartFetchPromise;
  }

  async function ensureCartCache() {
    if (!cartCache || !cartCache.items) {
      await hardFetchCart();
    }
    return cartCache;
  }

  async function refreshCartCacheFromServer() {
    await hardFetchCart();
    return cartCache;
  }

  /*****************************************
   * CART MUTATIONS
   *****************************************/
  async function changeLine({ key, quantity, properties }) {
    const body = { id: key };
    if (typeof quantity === 'number') body.quantity = quantity;
    if (properties) body.properties = properties;

    trace('changeLine() body', body);

    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });

    const json = await safeJson(res, 'changeLine() result');
    trace('changeLine() result', json);

    cartCache = json; // keep cache in sync with Shopify's response
    cartRevision += 1;
    return json;
  }

  async function addGiftWrap(quantity) {
    trace('addGiftWrap() body', { id: GIFT_CARD_VARIANT_ID, quantity });

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: GIFT_CARD_VARIANT_ID,
        quantity: quantity
      })
    });

    const added = await safeJson(res, 'addGiftWrap() result');
    trace('addGiftWrap() result(line item)', added);

    // refresh to canonical cart
    await refreshCartCacheFromServer();
    return cartCache;
  }

  async function removeLineByKey(key) {
    trace('removeLineByKey()', { key });
    await changeLine({ key, quantity: 0 });
    // cartCache updated by changeLine
  }

  async function addVariantClean(variant_id, quantity) {
    trace('addVariantClean()', { variant_id, quantity });

    // IMPORTANT: We add the variant WITHOUT _GiftWrap props here.
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variant_id,
        quantity: quantity
      })
    });

    await safeJson(res, 'addVariantClean() result');
    await refreshCartCacheFromServer();
  }

  /*****************************************
   * DRAWER REFRESH / OPEN
   *****************************************/
  function isDrawerOpen() {
    return !!document.querySelector('.slidecarthq.open')
        || !!document.querySelector('#slidecarthq .slidecarthq.open')
        || !!document.querySelector('#slidecarthq .slidecarthq-overlay.open');
  }

  // allowOpenIfClosed = false by default.
  // true means "user clicked Wrap as a gift / Remove gift wrapping"
  // false means "background sync, don't force-open drawer".
  async function refreshDrawerUsingCache(allowOpenIfClosed = false) {
    trace('refreshDrawerUsingCache()', {
      isOpen: isDrawerOpen(),
      allowOpenIfClosed,
      cartCache
    });

    await ensureCartCache();
    updateAncillaryUIFromCart(cartCache); // keep banner + bubble in sync on any refresh

    // Drawer already open → just sync content.
    if (isDrawerOpen()) {
      if (window.SLIDECART_SET_CART) {
        log('refreshDrawerUsingCache -> SET_CART (open)');
        window.SLIDECART_SET_CART(cartCache);
      } else if (window.SLIDECART_UPDATE) {
        log('refreshDrawerUsingCache -> UPDATE (open)');
        window.SLIDECART_UPDATE();
      }
      return;
    }

    // Drawer CLOSED
    if (!allowOpenIfClosed) {
      // Silent sync of data (no open)
      if (window.SLIDECART_SET_CART) {
        log('refreshDrawerUsingCache -> SET_CART silently (closed)');
        window.SLIDECART_SET_CART(cartCache);
      } else if (window.SLIDECART_UPDATE) {
        log('refreshDrawerUsingCache -> UPDATE silently (closed)');
        window.SLIDECART_UPDATE();
      }
      return;
    }

    // Caller explicitly wants to open:
    if (typeof window.emitCartCompat === 'function') {
      log('refreshDrawerUsingCache -> emitCartCompat(open+sync)');
      await window.emitCartCompat('gift-sync');
      return;
    }

    // Fallback manual open
    if (window.SLIDECART_SET_CART) {
      window.SLIDECART_SET_CART(cartCache);
    } else if (window.SLIDECART_UPDATE) {
      window.SLIDECART_UPDATE();
    }
    if (window.SLIDECART_OPEN) {
      window.SLIDECART_OPEN();
    }
  }

  /*****************************************
   * NOTE / PACKING INSTRUCTIONS
   *****************************************/
  function buildGiftWrapNote(items) {
    // Collect only normal lines that are flagged for gift wrap
    const flaggedLines = (items || []).filter(
      it => !isGiftWrapProduct(it) && isFlaggedForGift(it)
    );

    if (!flaggedLines.length) return '';

    const lines = flaggedLines.map(it => {
      const title = (it.title || it.handle || `Variant ${it.variant_id}`)
        .replace(/&quot;/g, '"')
        .trim();
      const type  = (it.product_type || '').trim();
      const qty   = it.quantity || 1;

      // Format: Title (Type) xQty  — omit parentheses if no type
      return type
        ? `- ${title} (${type}) x${qty}`
        : `- ${title} x${qty}`;
    });

    return `Gift wrap these:\n${lines.join('\n')}`;
  }

  async function updateCartNote(noteText) {
    const body = { note: noteText };

    trace('updateCartNote()', body);

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });

    const json = await safeJson(res, 'updateCartNote() result');
    trace('updateCartNote() result', json);

    cartCache = json; // keep in sync
    cartRevision += 1;
    return json;
  }

  /*****************************************
   * DETECT GIFT-WRAP ITEMS AND FLAGS
   *****************************************/
  function isGiftWrapProduct(item) {
    if (item.variant_id === GIFT_CARD_VARIANT_ID) return true;
    return false;
  }

  function isFlaggedForGift(item) {
    // we mark an item to be wrapped by giving it a property _GiftWrap: "yes"
    // (or similar giftwrap/gift_wrap)
    const p = item.properties || {};
    const v = (p._GiftWrap ?? p.giftwrap ?? p.gift_wrap ?? '')
      .toString()
      .toLowerCase()
      .trim();
    return v === 'yes' || v === 'true' || v === '1';
  }

  function countFlaggedUnits(items) {
    // how many total "wrappable" units are flagged
    return (items || []).reduce(
      (sum, it) =>
        sum + (isFlaggedForGift(it) && !isGiftWrapProduct(it) ? (it.quantity || 0) : 0),
      0
    );
  }

  function findGiftWrapLine(items) {
    return (items || []).find(isGiftWrapProduct) || null;
  }

  function computeFlagSummary(items) {
    // produce a snapshot of lines for visibility + sync decisions
    const rows = (items || []).map(it => ({
      key: it.key,
      variant_id: it.variant_id,
      title: it.title,
      handle: it.handle,
      quantity: it.quantity,
      isGiftWrap: isGiftWrapProduct(it),
      flagged: isFlaggedForGift(it),
      properties: it.properties || {}
    }));

    const flagged = rows.filter(r => !r.isGiftWrap && r.flagged);
    const requiredQty = flagged.reduce((sum, r) => sum + (r.quantity || 0), 0);

    const gw = rows.find(r => r.isGiftWrap);
    const currentQty = gw ? gw.quantity : 0;

    trace('FLAG SUMMARY', {
      flagged,
      requiredQty,
      gw: gw ? { key: gw.key, quantity: gw.quantity } : null,
      rows
    });

    return { flagged, requiredQty, gw, currentQty };
  }

  /*****************************************
   * ELIGIBILITY CHECK (EXCLUDE SOME PRODUCTS)
   *****************************************/
  // We mark products as NOT eligible for gift wrapping by having
  // <meta name="giftwrap:exclude" content="true"> on their product page.

  const ELIGIBILITY_STORAGE_KEY = `giftwrapEligibility:${window.Shopify?.theme?.id || 'main'}`;
  const eligibilityCache = new Map(); // handle -> boolean
  const pendingCache     = new Map(); // handle -> promise
  const eligibilityRenderWaiters = new Set();

  try {
    const storedEligibility = JSON.parse(
      window.sessionStorage.getItem(ELIGIBILITY_STORAGE_KEY) || '{}'
    );
    Object.entries(storedEligibility).forEach(([handle, eligible]) => {
      eligibilityCache.set(handle, eligible !== false);
    });
  } catch (_) {/* storage unavailable or stale data */}

  function persistEligibilityCache() {
    try {
      window.sessionStorage.setItem(
        ELIGIBILITY_STORAGE_KEY,
        JSON.stringify(Object.fromEntries(eligibilityCache))
      );
    } catch (_) {/* storage unavailable */}
  }

  // On product pages Liquid has already rendered the metafield into this meta
  // tag, so seed the cache without another product-page request.
  try {
    const currentProductMatch = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    const exclusionMeta = document.querySelector('meta[name="giftwrap:exclude"]');
    if (currentProductMatch && exclusionMeta) {
      const currentHandle = decodeURIComponent(currentProductMatch[1]);
      eligibilityCache.set(currentHandle, !asBool(exclusionMeta.content));
      persistEligibilityCache();
    }
  } catch (_) {/* malformed URL or metadata */}

  function asBool(v) {
    if (typeof v === 'boolean') return v;
    const s = String(v).trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }

  function buildProductBase(handle) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const abs = new URL(
      `${root.replace(/\/?$/, '/') }products/${encodeURIComponent(handle)}`,
      window.location.origin
    );

    // keep preview_theme_id (so preview theme sees its own metafields)
    const qs = new URLSearchParams(window.location.search);
    if (qs.has('preview_theme_id')) {
      abs.searchParams.set('preview_theme_id', qs.get('preview_theme_id'));
    } else if (
      window.Shopify?.theme?.role !== 'main' &&
      window.Shopify?.theme?.id
    ) {
      abs.searchParams.set('preview_theme_id', window.Shopify.theme.id);
    }

    return abs;
  }

  function buildProductHtmlUrl(handle) {
    return buildProductBase(handle).toString().replace(window.location.origin, '');
  }

  async function eligibilityViaHtml(handle) {
    const url = buildProductHtmlUrl(handle);
    const res = await fetch(url, { cache: 'default', credentials: 'same-origin' });
    const html = await res.text();

    const m = html.match(
      /<meta\s+name=["']giftwrap:exclude["']\s+content=["'](true|false|1|0|yes|no)["'][^>]*>/i
    );

    if (m) {
      const excluded = asBool(m[1]);
      const eligible = !excluded;
      log('eligibility via HTML META', handle, { excluded, eligible });
      return eligible;
    }

    log('no giftwrap:exclude meta on product HTML -> allow gift wrap', handle);
    return true;
  }

  async function giftwrapEligibleForHandle(handle) {
    if (!handle) return true;
    if (eligibilityCache.has(handle)) return eligibilityCache.get(handle);
    if (pendingCache.has(handle)) return pendingCache.get(handle);

    const p = (async () => {
      try {
        return await eligibilityViaHtml(handle);
      } catch (e) {
        warn('eligibility fallback allow', handle, e.message);
        return true;
      }
    })()
      .then(val => {
        eligibilityCache.set(handle, val);
        persistEligibilityCache();
        pendingCache.delete(handle);
        return val;
      })
      .catch(err => {
        pendingCache.delete(handle);
        warn('eligibility err; allow', handle, err);
        return true;
      });

    pendingCache.set(handle, p);
    return p;
  }

  function resolveEligibilityWithoutBlockingRender(handle) {
    if (!handle || eligibilityCache.has(handle) || eligibilityRenderWaiters.has(handle)) return;

    eligibilityRenderWaiters.add(handle);
    giftwrapEligibleForHandle(handle)
      .catch(() => true)
      .finally(() => {
        eligibilityRenderWaiters.delete(handle);
        scheduleButtonInjection();
      });
  }

  function prewarmEligibilityForCart(items) {
    const handles = new Set(
      (items || [])
        .filter((item) => !isGiftWrapProduct(item))
        .map((item) => item.handle)
        .filter(Boolean)
    );
    handles.forEach(resolveEligibilityWithoutBlockingRender);
  }

  /*****************************************
   * MATCH DOM NODE -> CART LINE
   *****************************************/
  function findCartLineForVariant(cartItems, variantId, domKeyMaybe) {
    // Prefer exact line key if SlideCart DOM gives us one
    if (domKeyMaybe) {
      const byKey = (cartItems || []).find(i => i.key === domKeyMaybe);
      if (byKey) return byKey;
    }
    // Otherwise pick first non-gift-wrap with this variant
    return (cartItems || []).find(
      i => i.variant_id === variantId && !isGiftWrapProduct(i)
    ) || null;
  }

  /*****************************************
   * SYNC GIFT-WRAP PRODUCT QTY + NOTE
   *****************************************/
  async function syncGiftWrapQuantityNow(force = false, cartAlreadyFresh = false) {
    // OFF MODE:
    // When the feature is disabled, we "clean up":
    // - remove _GiftWrap from lines
    // - remove gift wrap product
    // - clear the note
    if (!giftwrapFeatureOn()) {
      if (!cartAlreadyFresh) {
        await refreshCartCacheFromServer();
      }
      const items = cartCache.items || [];
      let cartChanged = false;

      // 1. remove all _GiftWrap / giftwrap / gift_wrap props from non-gift lines
      for (const item of items) {
        if (isGiftWrapProduct(item)) continue;
        if (!item.properties) continue;

        if (
          item.properties._GiftWrap ||
          item.properties.giftwrap ||
          item.properties.gift_wrap
        ) {
          const newProps = { ...item.properties };
          delete newProps._GiftWrap;
          delete newProps.giftwrap;
          delete newProps.gift_wrap;

          await changeLine({
            key: item.key,
            quantity: item.quantity,
            properties: newProps
          });
          cartChanged = true;
        }
      }

      // 2. remove gift wrap product if present
      await ensureCartCache();
      const afterNoFlags = cartCache.items || [];
      const wrapLine = findGiftWrapLine(afterNoFlags);
      if (wrapLine) {
        await removeLineByKey(wrapLine.key);
        cartChanged = true;
      }

      // 3. clear cart note
      if (String(cartCache.note || '') !== '') {
        await updateCartNote('');
        cartChanged = true;
      }

      return cartChanged;
    }

    // NORMAL MODE (feature ON):
    if (!cartAlreadyFresh) {
      await refreshCartCacheFromServer(); // /cart.js
    }

    const items = cartCache.items || [];
    const { requiredQty, gw, currentQty } = computeFlagSummary(items);
    let cartChanged = false;

    trace('syncGiftWrapQuantityNow()', {
      requiredQty,
      currentQty,
      lastSyncState,
      force
    });

    // If values didn't change and not forced → skip updates.
    if (
      !force &&
      lastSyncState.requiredQty === requiredQty &&
      lastSyncState.existingQty === currentQty
    ) {
      log('syncGiftWrapQuantityNow: already in sync, no writes');
    } else {
      // We might need to add / update / remove gift-wrap line
      if (requiredQty === 0) {
        if (gw) {
          log('sync: removing gift-wrap line (required 0)');
          await changeLine({ key: gw.key, quantity: 0 });
          cartChanged = true;
        }
      } else {
        if (gw) {
          if (gw.quantity !== requiredQty) {
            log(`sync: updating gift-wrap qty ${gw.quantity} -> ${requiredQty}`);
            await changeLine({ key: gw.key, quantity: requiredQty });
            cartChanged = true;
          } else {
            log('sync: gift-wrap qty already correct');
          }
        } else {
          log(`sync: adding gift-wrap line @ qty ${requiredQty}`);
          await addGiftWrap(requiredQty);
          cartChanged = true;
        }
      }
    }

    // Update lastSyncState snapshot
    {
      const afterItems = cartCache.items || [];
      const { requiredQty: req2, gw: gw2 } = computeFlagSummary(afterItems);
      lastSyncState = {
        requiredQty: req2,
        existingQty: gw2 ? gw2.quantity : 0
      };
    }

    // Build / update the packing note
    const noteText = buildGiftWrapNote(cartCache.items || []);
    const currentNote = String(cartCache.note || '');
    if (currentNote !== noteText) {
      await updateCartNote(noteText);
      cartChanged = true;
    }

    return cartChanged;
  }

  /*****************************************
   * USER ACTIONS - ADD GIFT WRAP TO A LINE
   *****************************************/
  async function flagLineAsGiftByVariant(variantId, domKeyMaybe) {
    if (!giftwrapFeatureOn()) return;

    await ensureCartCache();

    const line = findCartLineForVariant(cartCache.items, variantId, domKeyMaybe);
    if (!line) {
      warn('flagLineAsGiftByVariant: no line', variantId, domKeyMaybe);
      return;
    }

    // clone properties and set _GiftWrap: "yes"
    const newProps = Object.assign({}, line.properties || {}, {
      _GiftWrap: 'yes'
    });

    // write updated props back on this specific cart line
    await changeLine({
      key: line.key,
      quantity: line.quantity,
      properties: newProps
    });

    // re-sync gift wrap qty + order note
    await syncGiftWrapQuantityNow(true, true);

    // mark that we're doing an internal drawer refresh, so SLIDECART_UPDATED
    // won't double-run heavy work
    window.__GW_INTERNAL_REFRESH__ = true;

    // after user click -> allow opening if closed
    await refreshDrawerUsingCache(true);
    await injectButtonsForAllLines();
    styleGiftWrapOrder();
    normalizeOrdersFromDom();
    armObserverOnce();

    window.__GW_INTERNAL_REFRESH__ = false;
  }

  /*****************************************
   * USER ACTIONS - REMOVE GIFT WRAP FROM A LINE
   *****************************************/
  async function unflagLineGiftByVariant(variantId, domKeyMaybe) {
    if (!giftwrapFeatureOn()) return;

    await ensureCartCache();

    const line = findCartLineForVariant(cartCache.items, variantId, domKeyMaybe);
    if (!line) {
      warn('unflagLineGiftByVariant: no line', variantId, domKeyMaybe);
      return;
    }

    const qtyToKeep = line.quantity || 1;
    const lineKey   = line.key;

    // --- capture current visual order before removal
    const nodeBefore = document.querySelector(
      `#slidecarthq .items [data-variant-id="${variantId}"]`
    );
    if (nodeBefore) {
      normalizeOrdersFromDom(); // make sure everyone has an explicit order
      const keepOrder = currentOrderOfNode(nodeBefore);
      desiredOrderByVariant.set(variantId, keepOrder);
    }

    trace('unflagLineGiftByVariant: start', {
      variantId,
      lineKey,
      qtyToKeep,
      propsBefore: line.properties
    });

    // Shopify can merge lines in weird ways when editing properties.
    // The most reliable "unflag" is:
    // 1. Remove the flagged line entirely.
    await removeLineByKey(lineKey);

    // 2. Re-add the same variant clean (no _GiftWrap prop).
    await addVariantClean(variantId, qtyToKeep);

    // 3. Sync gift wrap product qty + order note (and cleanup note if last)
    await syncGiftWrapQuantityNow(true, true);

    // internal refresh guard
    window.__GW_INTERNAL_REFRESH__ = true;

    // after user click -> allow opening if closed
    await refreshDrawerUsingCache(true);
    await injectButtonsForAllLines();
    styleGiftWrapOrder();

    // --- re-apply remembered order to the new node
    const remembered = desiredOrderByVariant.get(variantId);
    if (Number.isFinite(remembered)) {
      setNodeOrderForVariant(variantId, remembered);
      // you can clear it if you want:
      // desiredOrderByVariant.delete(variantId);
    }

    normalizeOrdersFromDom();
    armObserverOnce();

    window.__GW_INTERNAL_REFRESH__ = false;
  }

  /*****************************************
   * INLINE UI HELPERS (single-button version)
   *****************************************/
  let __gwBatchStyle = false;
  function ensureBatchStyle() {
    if (__gwBatchStyle) return;
    const css = document.createElement('style');
    css.textContent = `
      /* One element: the button */
      #slidecarthq .slidecarthq button.gw-toggle-btn {
        appearance: none;
        border: none;
        color:#008046;
        text-decoration:none;
        background: #fff;
        padding: 4px 8px;
        font-size: 12px;
        line-height: 1.4;
        cursor: pointer;
        transition: opacity .18s ease;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-height: 24px;
        margin-left:-7px;
        margin-top:-2px;
      }
      #slidecarthq .slidecarthq button.gw-toggle-btn[data-state="idle"] {
        margin-left:1px;
      }
      #slidecarthq .slidecarthq button.gw-toggle-btn[data-state="idle"]::before {
        content:"+";
        display:inline-block;
        position:absolute;
        margin-left:-10px;
      }
      #slidecarthq .slidecarthq button.gw-toggle-btn[disabled] {
        opacity: .4;
        cursor: default;
      }
      #slidecarthq .slidecarthq button.gw-toggle-btn[data-giftwrap-eligibility="pending"] {
        opacity: 1;
        cursor: wait;
      }

      /* Spinner via pseudo-element (not an extra DOM node) */
      #slidecarthq .slidecarthq button.gw-toggle-btn[data-state="adding"]::before,
      #slidecarthq .slidecarthq button.gw-toggle-btn[data-state="removing"]::before {
        content: '';
        width: 12px; height: 12px;
        border-radius: 50%;
        border: 2px solid currentColor;
        border-top-color: transparent;
        display: inline-block;
        animation: giftwrap-spin .6s linear infinite;
      }
      @keyframes giftwrap-spin { to { transform: rotate(360deg); } }

      /* Keep gift wrap product visually last */
      #slidecarthq .items [data-variant-id="${GIFT_CARD_VARIANT_ID}"] { order: 999; }
    `;
    document.head.appendChild(css);
    __gwBatchStyle = true;
  }

  function styleGiftWrapOrder() {
    const gwNode = document.querySelector(
      `#slidecarthq .items [data-variant-id="${GIFT_CARD_VARIANT_ID}"]`
    );
    if (gwNode) {
      gwNode.style.order = '999';
    }
  }

  function giftWrapButtonsForTarget(mainTarget) {
    return Array.from(mainTarget.children)
      .filter(node => node.classList?.contains('gw-toggle-btn'));
  }

  function removeToggleButtons(mainTarget) {
    giftWrapButtonsForTarget(mainTarget).forEach(btn => btn.remove());
  }

  function ensureToggleButton(mainTarget, id) {
    const buttons = giftWrapButtonsForTarget(mainTarget);
    let btn = buttons.find(candidate => candidate.id === id) || buttons[0];

    // Shopify changes a line item's key when _GiftWrap is added/removed. Reuse
    // the row's existing control and remove stale copies created for older keys.
    buttons.forEach(candidate => {
      if (candidate !== btn) candidate.remove();
    });

    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gw-toggle-btn';
      mainTarget.appendChild(btn);
    }

    if (btn.id !== id) btn.id = id;
    return btn;
  }

  function setBtnState(btn, state, label, disabled = false) {
    if (btn.getAttribute('data-state') !== state) {
      btn.setAttribute('data-state', state);
    }
    if (btn.textContent !== label) {
      btn.textContent = label;
    }
    if (btn.disabled !== !!disabled) {
      btn.disabled = !!disabled;
    }
  }

  /*****************************************
   * STABLE ORDER HELPERS
   *****************************************/
  const GW_ORDER_ATTR = 'data-gw-fixed-order';
  const desiredOrderByVariant = new Map(); // variantId -> number

  function cartItemNodes() {
    // All item rows except the gift-wrap product
    return Array.from(document.querySelectorAll('#slidecarthq .items [data-variant-id]'))
      .filter(n => Number(n.getAttribute('data-variant-id')) !== GIFT_CARD_VARIANT_ID);
  }

  function normalizeOrdersFromDom() {
    // Give every row an explicit order equal to current DOM order (only once per node)
    const nodes = cartItemNodes();
    nodes.forEach((node, idx) => {
      if (!node.hasAttribute(GW_ORDER_ATTR)) {
        node.style.order = String(idx);
        node.setAttribute(GW_ORDER_ATTR, '1');
      }
    });
  }

  function setNodeOrderForVariant(variantId, orderNum) {
    const node = document.querySelector(`#slidecarthq .items [data-variant-id="${variantId}"]`);
    if (node && Number.isFinite(orderNum)) {
      node.style.order = String(orderNum);
      node.setAttribute(GW_ORDER_ATTR, '1');
    }
  }

  function currentOrderOfNode(node) {
    // Use explicit order if present; otherwise use DOM index
    const explicit = Number(node.style.order);
    if (Number.isFinite(explicit)) return explicit;
    const siblings = cartItemNodes();
    return Math.max(0, siblings.indexOf(node));
  }

  /*****************************************
   * BUTTON INJECTION FOR ALL LINES (single element)
   *****************************************/
  let __gwInjecting = false;
  let __gwRerunRequested = false;

  async function injectButtonsForAllLines() {
    if (__gwInjecting) {
      __gwRerunRequested = true;
      return;
    }
    __gwInjecting = true;

    try {
      await ensureCartCache();

      // If feature is OFF, remove buttons and bail
      if (!giftwrapFeatureOn()) {
        document.querySelectorAll('.gw-toggle-btn').forEach(n => n.remove());
        return;
      }

      ensureBatchStyle();

      const cartItems = cartCache.items || [];
      const domNodes = Array.from(
        document.querySelectorAll('#slidecarthq [data-variant-id]')
      );

      // PASS 1: collect candidate rows
      const candidates = [];
      for (const node of domNodes) {
        const variantId   = Number(node.getAttribute('data-variant-id'));
        const domKeyMaybe = node.getAttribute('data-line-item-key') || '';

        const lineItem = findCartLineForVariant(cartItems, variantId, domKeyMaybe);
        if (!lineItem) continue;
        if (isGiftWrapProduct(lineItem)) continue; // skip global gift-wrap product

        const mainTarget = node.querySelector('.item-container .main');
        if (!mainTarget) continue;

        candidates.push({ variantId, domKeyMaybe, lineItem, mainTarget });
      }

      // Render each row immediately. Eligibility is resolved independently in
      // the background so one slow product page never blocks every cart row.
      for (const c of candidates) {
        const { variantId, domKeyMaybe, lineItem, mainTarget } = c;
        const handle = lineItem.handle || '';
        const eligibilityKnown = !handle || eligibilityCache.has(handle);
        const eligible = !handle || eligibilityCache.get(handle) !== false;

        const lineIdentity = String(domKeyMaybe || variantId)
          .replace(/[^a-zA-Z0-9_-]/g, '-');
        const btnId = `giftwrap-toggle-${lineIdentity}`;

        if (!eligible) {
          removeToggleButtons(mainTarget);
          continue;
        }

        const btn = ensureToggleButton(mainTarget, btnId);
        btn.dataset.giftwrapEligibility = eligibilityKnown ? 'eligible' : 'pending';

        // Initial label/state
        if (isFlaggedForGift(lineItem)) {
          setBtnState(btn, 'flagged', 'Remove gift wrapping', !eligibilityKnown);
        } else {
          setBtnState(btn, 'idle', 'Wrap as a gift', !eligibilityKnown);
        }

        resolveEligibilityWithoutBlockingRender(handle);

        // Single handler
        btn.onclick = async () => {
          const currentLine =
            findCartLineForVariant(cartCache.items || [], variantId, domKeyMaybe) || {};
          const currentlyFlagged = isFlaggedForGift(currentLine);

          if (!currentlyFlagged) {
            setBtnState(btn, 'adding', 'Adding gift wrapping…', true);
            try {
              await flagLineAsGiftByVariant(variantId, domKeyMaybe);
              setBtnState(btn, 'flagged', 'Remove gift wrapping', false);
            } catch (e) {
              err('flag error', e);
              setBtnState(btn, 'idle', 'Wrap as a gift', false);
            }
          } else {
            setBtnState(btn, 'removing', 'Removing gift wrapping…', true);
            try {
              await unflagLineGiftByVariant(variantId, domKeyMaybe);
              setBtnState(btn, 'idle', 'Wrap as a gift', false);
            } catch (e) {
              err('unflag error', e);
              setBtnState(btn, 'flagged', 'Remove gift wrapping', false);
            }
          }

          styleGiftWrapOrder();
          normalizeOrdersFromDom();
        };
      }

      styleGiftWrapOrder();
      normalizeOrdersFromDom();

    } catch (e) {
      warn('injectButtonsForAllLines failed', e);
    } finally {
      __gwInjecting = false;
      if (__gwRerunRequested) {
        __gwRerunRequested = false;
        scheduleButtonInjection();
      }
    }
  }

  /*****************************************
   * MUTATION OBSERVER / CART HOOKS
   *****************************************/
  let injectionScheduled = false;
  let observedSlideCartRoot = null;

  function scheduleButtonInjection() {
    if (injectionScheduled) return;
    injectionScheduled = true;

    queueMicrotask(async () => {
      injectionScheduled = false;
      await injectButtonsForAllLines();
      styleGiftWrapOrder();
      normalizeOrdersFromDom();
    });
  }

  function isGiftWrapButtonMutation(mutation) {
    const target = mutation.target.nodeType === Node.ELEMENT_NODE
      ? mutation.target
      : mutation.target.parentElement;

    // Text changes made inside an existing control are our own work.
    if (target?.matches?.('.gw-toggle-btn') || target?.closest?.('.gw-toggle-btn')) {
      return true;
    }

    // Ignore our own button insertion. A button removal is deliberately not
    // ignored: that can mean SlideCart just re-rendered a row and we must restore it.
    return mutation.removedNodes.length === 0 &&
      mutation.addedNodes.length > 0 &&
      [...mutation.addedNodes].every((node) =>
        node.nodeType === Node.ELEMENT_NODE && node.matches?.('.gw-toggle-btn')
      );
  }

  const mo = new MutationObserver((mutations) => {
    if (mutations.length && mutations.every(isGiftWrapButtonMutation)) return;
    trace('MutationObserver scheduling gift-wrap controls', mutations.length);
    scheduleButtonInjection();
  });

  function ensureObserver() {
    if (!giftwrapFeatureOn()) {
      mo.disconnect();
      observedSlideCartRoot = null;
      return;
    }

    const root = document.querySelector(SLIDECART_ROOT_SELECTOR);
    if (root === observedSlideCartRoot) return;

    mo.disconnect();
    observedSlideCartRoot = root;

    if (!root) {
      log('MutationObserver waiting for SlideCart root');
      return;
    }

    mo.observe(root, { childList: true, subtree: true });
    log('MutationObserver attached to stable SlideCart root');
    scheduleButtonInjection();
  }

  // Kept as a compatibility helper for existing call sites. The observer now
  // remains attached to SlideCart's stable outer root instead of timing out.
  function armObserverOnce() {
    ensureObserver();
    scheduleButtonInjection();
  }

  // SlideCart appends #slidecarthq directly to <body>. Watching only direct
  // body children is cheap and lets us reconnect if the app replaces its root.
  const slideCartRootObserver = new MutationObserver(ensureObserver);
  if (document.body) {
    slideCartRootObserver.observe(document.body, { childList: true });
  }

  /*****************************************
   * ANCILLARY UI (free shipping + cart bubble)
   *****************************************/
  function updateAncillaryUIFromCart(cart) {
    try {
      if (!cart || !cart.items) return;

      // ---- Free shipping banner inside the drawer
      if (cart.total_price > 0) {
        const announcementBar = document.querySelector('.announcement-bar');
        const shippingInfo    = document.querySelector('#slidecarthq .free-shipping');
        const customHtmlWrap  = document.querySelector('#slidecarthq .custom-html');

        if (announcementBar && (shippingInfo || customHtmlWrap)) {
          const cur = String(cart.currency || '').toLowerCase();
          const datasetName = 'freeShipping' + (cur.charAt(0).toUpperCase() + cur.slice(1));
          const freeShippingLimit = Number(announcementBar.dataset[datasetName]);

          if (freeShippingLimit) {
            const cartValue = (cart.total_price || 0) / 100;
            let orderText   = 'Order qualifies for free shipping!';
            if (cartValue < freeShippingLimit) {
              const leftToFree = Math.max(0, freeShippingLimit - cartValue);
              const leftStr = leftToFree % 1 === 0 ? leftToFree.toFixed(0) : leftToFree.toFixed(2);
              orderText = `Only ${leftStr} ${cart.currency} away from free shipping!`;
            }
            if (shippingInfo) shippingInfo.textContent = orderText;
            if (customHtmlWrap) customHtmlWrap.style.display = '';
          } else {
            if (customHtmlWrap) customHtmlWrap.style.display = 'none';
          }
        }
      }

      // ---- Header cart-count bubble
      const headerCartIcon = document.querySelector('.header__icon--cart');
      const existingBubble = document.querySelector('.cart-count-bubble');
      const count = cart.items.length;

      if (!existingBubble && count && headerCartIcon) {
        const bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.textContent = String(count);
        headerCartIcon.appendChild(bubble);
      } else if (existingBubble && !count) {
        existingBubble.remove();
      } else if (existingBubble && count) {
        existingBubble.textContent = String(count);
      }
    } catch (e) {
      warn('updateAncillaryUIFromCart failed', e);
    }
  }

  /*****************************************
   * SLIDECART HOOKS (cart-param compatible)
   *****************************************/
  window.SLIDECART_UPDATED = async function(passedCart) {
    try {
      // If we've just internally refreshed after a button click,
      // don't run the full sync again.
      if (window.__GW_INTERNAL_REFRESH__) {
        window.__GW_INTERNAL_REFRESH__ = false;
        log('SLIDECART_UPDATED skipped (internal refresh)');
        if (passedCart?.items) {
          cartCache = passedCart;
          cartRevision += 1;
          prewarmEligibilityForCart(passedCart.items);
          updateAncillaryUIFromCart(passedCart);
        }
        armObserverOnce();
        return;
      }

      // Prefer the theme-provided cart if present; otherwise fetch fresh
      let cartObj = passedCart;
      if (!cartObj || !cartObj.items) {
        await refreshCartCacheFromServer();
        cartObj = cartCache;
      } else {
        // keep our cache in sync with the passed cart so downstream logic works
        cartCache = cartObj;
        cartRevision += 1;
      }

      // Update the banner + bubble ASAP
      prewarmEligibilityForCart(cartObj.items);
      updateAncillaryUIFromCart(cartObj);
      ensureObserver();
      scheduleButtonInjection();

      if (!giftwrapFeatureOn()) {
        // Feature OFF:
        log('SLIDECART_UPDATED (giftwrap disabled) -> silent sync only');
        const cartChanged = await syncGiftWrapQuantityNow(
          true /* force cleanup while off */,
          true /* cartAlreadyFresh */
        );
        if (cartChanged) {
          await refreshDrawerUsingCache(false); // do not open drawer
        }
        updateAncillaryUIFromCart(cartCache); // totals may have changed
        return;
      }

      log('SLIDECART_UPDATED hook (feature ON)');

      // Reconcile in the background after scheduling the controls. For the
      // common case (an ordinary unwrapped add), this performs zero requests
      // and avoids a second SlideCart render entirely.
      const cartChanged = await syncGiftWrapQuantityNow(
        false /*force*/,
        true /*cartAlreadyFresh*/
      );

      if (cartChanged) {
        await refreshDrawerUsingCache(false);
      }

      armObserverOnce();

      // final ancillary UI pass in case totals changed during sync
      updateAncillaryUIFromCart(cartCache);

    } catch (e) {
      warn('SLIDECART_UPDATED failed', e);
    }
  };

  window.SLIDECART_OPENED = function() {
    log('SLIDECART_OPENED hook');
    ensureObserver();
    scheduleButtonInjection();
  };

  /*****************************************
   * DEBUG HELPERS (console)
   *****************************************/
  // Allows custom add-to-cart flows to warm the metafield lookup while their
  // own product/cart requests are already in flight.
  window.GIFTWRAP_PRELOAD_ELIGIBILITY = giftwrapEligibleForHandle;

  window.gwDebug = {
    cart:        () => cartCache,
    refetch:     hardFetchCart,
    summary:     () => computeFlagSummary(cartCache?.items || []),
    syncNow:     (force=false) => syncGiftWrapQuantityNow(force),
    inject:      injectButtonsForAllLines,
    addGiftById: flagLineAsGiftByVariant,
    removeGiftById: unflagLineGiftByVariant,
    normalizeOrdersFromDom,
    setNodeOrderForVariant
  };

  /*****************************************
   * BOOTSTRAP (run once on load)
   *****************************************/
  (async function init() {
    // Install styles and start watching before the first cart request so rows
    // created by SlideCart can be hydrated before their first paint.
    ensureBatchStyle();
    ensureObserver();

    await ensureCartCache();
    prewarmEligibilityForCart(cartCache.items);
    updateAncillaryUIFromCart(cartCache); // keep header bubble & banner right away

    if (!giftwrapFeatureOn()) {
      log('giftwrap disabled at init()');
      // run cleanup path immediately to:
      // - clear _GiftWrap props
      // - remove gift wrap line
      // - clear note
      const cartChanged = await syncGiftWrapQuantityNow(
        true /*force*/,
        true /*cartAlreadyFresh*/
      );
      if (cartChanged) {
        await refreshDrawerUsingCache(false);
      }
      // do not inject buttons while the feature is disabled
      return;
    }

    // feature ON: establish baseline sync snapshot
    const initItems = cartCache.items || [];
    {
      const { requiredQty, gw } = computeFlagSummary(initItems);
      lastSyncState = {
        requiredQty,
        existingQty: gw ? gw.quantity : 0
      };
    }

    // Render immediately if rows already exist; otherwise the persistent root
    // observer hydrates them as soon as SlideCart inserts them.
    scheduleButtonInjection();
  })();

})();
